import React, { useEffect, useMemo, useState } from "react";

import {
  OnePagePortfolioAssessmentAnswer,
  OnePagePortfolioAssessment,
  ReferenceIssues,
} from "../../../state";
import Modal from "../../../atoms/Modal";
import Button from "../../../atoms/ActiveLearning/Button";
import { ReactComponent as CloseIcon } from "../../../assets/svg/oppa-after-modal_close.svg";

import Question from "./Question";
import Reference from "./Reference";
import styles from "./AfterModal.module.scss";

export type AfterModalProps = {
  isTeacher?: boolean;
  assessmentId: number;
  assessmentData: OnePagePortfolioAssessment | null;
  referenceIssues: ReferenceIssues[];
  calling?: boolean;
  fetchOnePagePortfolioAssessment?: (id: number) => void;
  registerOnePagePortfolioAssessmentAnswers?: (
    id: number,
    references: OnePagePortfolioAssessmentAnswer[],
  ) => void;
  onClose: () => void;
};

const AfterModal: React.FC<AfterModalProps> = ({
  isTeacher,
  assessmentId,
  assessmentData,
  referenceIssues,
  fetchOnePagePortfolioAssessment,
  registerOnePagePortfolioAssessmentAnswers,
  onClose,
}) => {
  const [answer01, setAnswer01] = useState<string>("");
  const [answer02, setAnswer02] = useState<string>("");

  useEffect(() => {
    if (!fetchOnePagePortfolioAssessment) return;

    fetchOnePagePortfolioAssessment(assessmentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  const assessmentTitle = useMemo(() => {
    const title = assessmentData?.title ?? "";
    return title.replace(/\s*-高校-|\s*-中学-/g, "");
  }, [assessmentData]);

  const referenceTitleBeforeAnswer = useMemo(() => {
    return (
      assessmentData?.reference_one_page_portfolio_assessment_issues[0].title ??
      ""
    );
  }, [assessmentData]);

  const question01 = useMemo(() => {
    const issue = assessmentData?.one_page_portfolio_assessment_issues?.[0];
    const question = issue?.one_page_portfolio_assessment_questions?.[0];

    return {
      id: question?.id ?? 0,
      title: question?.title ?? "",
      answer: question?.one_page_portfolio_assessment_answer?.contents ?? "",
    };
  }, [assessmentData]);

  const question02 = useMemo(() => {
    const issue = assessmentData?.one_page_portfolio_assessment_issues?.[1];
    const question = issue?.one_page_portfolio_assessment_questions?.[0];

    return {
      id: question?.id ?? 0,
      title: question?.title ?? "",
      answer: question?.one_page_portfolio_assessment_answer?.contents ?? "",
    };
  }, [assessmentData]);

  useEffect(() => {
    setAnswer01(question01.answer);
    setAnswer02(question02.answer);
  }, [question01.answer, question02.answer]);

  const handleSubmit = () => {
    if (!registerOnePagePortfolioAssessmentAnswers) return;

    const assessmentAnswer: OnePagePortfolioAssessmentAnswer[] = [
      {
        one_page_portfolio_assessment_question_id: question01.id,
        contents: answer01,
      },
      {
        one_page_portfolio_assessment_question_id: question02.id,
        contents: answer02,
      },
    ];
    registerOnePagePortfolioAssessmentAnswers(assessmentId, assessmentAnswer);
  };

  return (
    <Modal onClose={onClose} className={styles.afterModal}>
      <CloseIcon className={styles.closeIcon} onClick={onClose} />
      <div className={styles.wrapper}>
        <p className={styles.title}>{assessmentTitle}</p>
        <div className={styles.section}>
          <Question
            no={"①"}
            title={question01.title}
            value={answer01}
            disabled={isTeacher ?? false}
            onChange={e => setAnswer01(e.target.value)}
          />
          <Reference
            title={"振り返りワークの回答"}
            referenceData={referenceIssues}
          />
        </div>
        <div className={styles.section}>
          <Question
            no={"②"}
            title={question02.title}
            value={answer02}
            disabled={isTeacher ?? false}
            onChange={e => setAnswer02(e.target.value)}
          />
          <Reference
            title={referenceTitleBeforeAnswer}
            referenceData={
              assessmentData?.reference_one_page_portfolio_assessment_issues ??
              []
            }
          />
        </div>
        {!isTeacher && (
          <Button handleClick={handleSubmit}>回答を送信する</Button>
        )}
      </div>
    </Modal>
  );
};

export default AfterModal;
